import { useEffect, useCallback } from "react";
import { ChatInputHandle } from "../components/ChatInput";

type InputFocusManagerParams = {
  chatInputRef: React.RefObject<ChatInputHandle>;
  onTypingStop: () => void;
  onChatActive: () => void;
};

export function useInputFocusManager({
  chatInputRef,
  onTypingStop,
  onChatActive,
}: InputFocusManagerParams) {
  const stopTypingAndMarkActive = useCallback(() => {
    onTypingStop();
    onChatActive();
  }, [onTypingStop, onChatActive]);

  // Android keyboard detection - when viewport expands, keyboard likely closed
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    let lastHeight = viewport.height;

    const handleViewportResize = () => {
      const currentHeight = viewport.height;
      const heightIncreased = currentHeight - lastHeight > 120;

      lastHeight = currentHeight;

      if (!heightIncreased) return;

      const activeElement = document.activeElement;
      const inputIsStillFocused =
        activeElement instanceof HTMLInputElement &&
        activeElement.classList.contains("input-message");

      if (!inputIsStillFocused) return;

      console.log("⌨️ Android keyboard hidden; blurring chat input");

      chatInputRef.current?.blur();

      // Important:
      // Keyboard closing should stop typing, but keyboard opening/focus
      // should not start typing.
      stopTypingAndMarkActive();
    };

    viewport.addEventListener("resize", handleViewportResize);

    return () => {
      viewport.removeEventListener("resize", handleViewportResize);
    };
  }, [chatInputRef, stopTypingAndMarkActive]);

  // Click outside input area to blur
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const clickedInsideInputArea = Boolean(
        target.closest(".input-container"),
      );

      if (clickedInsideInputArea) return;

      const activeElement = document.activeElement;
      const inputIsFocused =
        activeElement instanceof HTMLInputElement &&
        activeElement.classList.contains("input-message");

      if (!inputIsFocused) return;

      chatInputRef.current?.blur();
      stopTypingAndMarkActive();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [chatInputRef, stopTypingAndMarkActive]);

  return {
    stopTypingAndMarkActive,
  };
}